import type {
  AutoReloadResponse,
  BatchUpdateProductsParams,
  BatchUpdateProductsResponse,
  BillingEngineSubscription,
  BillingProfile,
  BillingProfileResponse,
  CreateSetupIntentResponse,
  CreateSubscriptionCheckoutParams,
  CreateSubscriptionCheckoutResponse,
  CreateWalletCheckoutParams,
  CreateWalletCheckoutResponse,
  CreditPurchase,
  CreditPurchaseOptionsResponse,
  CreditPurchasesResponse,
  PaymentMethodResponse,
  PlanResponse,
  PlansResponse,
  SavePaymentMethodParams,
  SavePaymentMethodResponse2,
  SubscriptionResponse,
  UpdateAutoReloadParams,
  UpdateAutoReloadResponse,
  WalletCheckoutStatusResponse,
  WalletResponse,
  WalletTransactionsResponse,
} from '@/src/types/billingEngine'

import { CSRF } from '../utils/CSRF'
import { BaseRepository } from './BaseRepository'

interface WalletTransactionsParams {
  limit?: number
  page?: number
  per_page?: number
}

/**
 * Billing Engine API Repository
 * Handles all API calls to the billing engine endpoints
 */
export class BillingEngineRepository extends BaseRepository {
  getBillingProfile() {
    return super.getHttpClientInstance().get<BillingProfileResponse>('/billing/api/v1/billing_profile')
  }

  updateBillingProfile(billing_profile: BillingProfile) {
    return super.getHttpClientInstance().patch<BillingProfileResponse>('/billing/api/v1/billing_profile', { billing_profile }, { 'X-CSRF-Token': CSRF.getToken() })
  }

  getCreditPurchases(page: number, perPage: number) {
    return super.getHttpClientInstance().get<CreditPurchasesResponse>(`/billing/api/v1/wallet/purchases?page=${page}&per_page=${perPage}`)
  }

  createPurchaseInvoice(id: number, billing_details: BillingProfile) {
    return super.getHttpClientInstance().post<{ purchase: CreditPurchase }>(`/billing/api/v1/wallet/purchases/${id}/invoice`, { billing_details }, { 'X-CSRF-Token': CSRF.getToken() })
  }

  getPurchaseReceipt(id: number) {
    return super.getHttpClientInstance().get<{ receipt_url: string }>(`/billing/api/v1/wallet/purchases/${id}/receipt`)
  }

  /**
   * Get all active self-serve plans
   */
  getPlans() {
    return super.getHttpClientInstance().get<PlansResponse>('/billing/api/v1/plans', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get a specific plan by slug
   */
  getPlan(slug: string) {
    return super.getHttpClientInstance().get<PlanResponse>(`/billing/api/v1/plans/${slug}`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get the current team's subscription
   */
  getCurrentSubscription() {
    return super.getHttpClientInstance().get<SubscriptionResponse>('/billing/api/v1/subscription', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Create a new subscription with checkout
   */
  createSubscriptionCheckout(params: CreateSubscriptionCheckoutParams) {
    return super.getHttpClientInstance().post<CreateSubscriptionCheckoutResponse>('/billing/api/v1/subscription', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Cancel the current subscription at the end of the billing period
   */
  cancelSubscription() {
    return super.getHttpClientInstance().delete<SubscriptionResponse>('/billing/api/v1/subscription/cancel', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Reactivate a subscription that was marked for cancellation
   */
  reactivateSubscription() {
    return super.getHttpClientInstance().post<SubscriptionResponse>(
      '/billing/api/v1/subscription/reactivate',
      {},
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  /**
   * Change the current subscription to a new plan
   * Uses Stripe's subscription update with automatic proration
   */
  changePlan(params: { plan_slug: string; product_quantities?: Record<string, number> }) {
    return super.getHttpClientInstance().patch<SubscriptionResponse>('/billing/api/v1/subscription/change_plan', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get checkout status
   */
  getCheckoutStatus(subscriptionId: number) {
    return super
      .getHttpClientInstance()
      .get<{ subscription_id: number; status: string; activated_at: string | null; checkout_url: string | null }>(`/billing/api/v1/checkouts/status/${subscriptionId}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
  }

  /**
   * Confirm payment after 3DS authentication and sync local DB
   */
  confirmPayment(params: { payment_intent_id: string }) {
    return super.getHttpClientInstance().post<{ subscription: BillingEngineSubscription }>('/billing/api/v1/subscription/confirm_payment', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Batch update subscription product quantities
   */
  batchUpdateProductQuantities(params: BatchUpdateProductsParams) {
    return super.getHttpClientInstance().patch<BatchUpdateProductsResponse>('/billing/api/v1/subscription/products', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get wallet balance and statistics
   */
  getWallet() {
    return super.getHttpClientInstance().get<WalletResponse>('/billing/api/v1/wallet', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get wallet transaction history
   */
  getWalletTransactions(params?: number | WalletTransactionsParams) {
    const searchParams = new URLSearchParams()

    if (typeof params === 'number') {
      searchParams.set('limit', params.toString())
    } else if (params) {
      if (params.limit !== undefined) searchParams.set('limit', params.limit.toString())
      if (params.page !== undefined) searchParams.set('page', params.page.toString())
      if (params.per_page !== undefined) searchParams.set('per_page', params.per_page.toString())
    }

    const queryString = searchParams.toString()
    const query = queryString ? `?${queryString}` : ''

    return super.getHttpClientInstance().get<WalletTransactionsResponse>(`/billing/api/v1/wallet/transactions${query}`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  getWalletTransactionsExportUrl() {
    return '/billing/api/v1/wallet/transactions/export.csv'
  }

  /**
   * Get credit purchase options
   */
  getCreditPurchaseOptions() {
    return super.getHttpClientInstance().get<CreditPurchaseOptionsResponse>('/billing/api/v1/wallet/purchase_options', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Create checkout session for wallet top-up
   */
  createWalletCheckout(params: CreateWalletCheckoutParams) {
    return super.getHttpClientInstance().post<CreateWalletCheckoutResponse>('/billing/api/v1/wallet/checkout', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get checkout status for credit purchase
   */
  getWalletCheckoutStatus(creditPurchaseId: number) {
    return super.getHttpClientInstance().get<WalletCheckoutStatusResponse>(`/billing/api/v1/wallet/checkout/${creditPurchaseId}/status`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Get auto-reload settings
   */
  getAutoReloadSettings() {
    return super.getHttpClientInstance().get<AutoReloadResponse>('/billing/api/v1/wallet/auto_reload', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Update auto-reload settings
   */
  updateAutoReloadSettings(params: UpdateAutoReloadParams) {
    return super.getHttpClientInstance().put<UpdateAutoReloadResponse>('/billing/api/v1/wallet/auto_reload', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  // ============================================
  // Payment Method endpoints (independent resource)
  // ============================================

  /**
   * Get current payment method
   */
  getPaymentMethod() {
    return super.getHttpClientInstance().get<PaymentMethodResponse>('/billing/api/v1/payment_method', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Create a SetupIntent for collecting a payment method
   */
  createPaymentMethodSetupIntent() {
    return super.getHttpClientInstance().post<CreateSetupIntentResponse>(
      '/billing/api/v1/payment_method/setup_intent',
      {},
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  /**
   * Save payment method from completed SetupIntent
   */
  savePaymentMethod(params: SavePaymentMethodParams) {
    return super.getHttpClientInstance().post<SavePaymentMethodResponse2>('/billing/api/v1/payment_method/save', params, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  /**
   * Remove saved payment method
   */
  removePaymentMethod() {
    return super.getHttpClientInstance().delete<PaymentMethodResponse>('/billing/api/v1/payment_method', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }
}

export const billingEngineRepository = new BillingEngineRepository()
